import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import type { Severity } from '@/lib/types'

/**
 * Public-facing supply-chain incident card. Reused across surfaces 1
 * (`/threats/supply-chain`), 3 (Provider detail timeline tab), and 4 (CVE
 * detail cross-ref). Mirrors the public list shape projected by
 * `projectIncidentForList()` + `buildIncidentListResponse()` in
 * `apps/api/src/supply-chain/public-routes.ts` — `raw`, `suppressedBy`, and
 * `suppressionReason` are stripped at the projector and have no place in
 * this contract. Even if a future API regression accidentally leaks them,
 * this component must never read them.
 *
 * `publishedAt` is the ISO-8601 string emitted by `res.json()` on the API
 * (Date → JSON serialises to string); we wrap it in `new Date()` for
 * `formatDistanceToNow`.
 *
 * Spec: §5.7 supply-chain v1 design doc.
 */
export interface SupplyChainIncident {
  id: string
  providerId: string
  providerSlug: string
  providerName: string
  providerCategory: 'cloud' | 'identity'
  source: string
  externalId: string
  title: string
  description: string | null
  url: string
  publishedAt: string
  lastUpdatedAt: string | null
  severityRaw: string | null
  severityNormalised: 'low' | 'medium' | 'high' | 'critical' | null
  isSecurityRelevant: boolean
  classificationMeta: unknown
  relatedCves: string[] | null
  suppressed: boolean
  suppressedAt: string | null
  retrievedAt: string
}

export function IncidentCard({ incident }: { incident: SupplyChainIncident }) {
  const cves = incident.relatedCves ?? []
  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/providers/${incident.providerSlug}`}
            className="font-semibold hover:underline"
          >
            {incident.providerName}
          </Link>
          <div className="flex flex-wrap items-center gap-1.5">
            {incident.severityNormalised && (
              <SeverityBadge severity={incident.severityNormalised as Severity} />
            )}
            {incident.isSecurityRelevant && (
              <Badge variant="outline" className="text-xs">
                security
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {incident.source}
            </Badge>
          </div>
        </div>

        <div className="font-medium">{incident.title}</div>

        {incident.description && (
          <div className="line-clamp-2 text-sm text-muted-foreground">
            {incident.description}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {formatDistanceToNow(new Date(incident.publishedAt), {
              addSuffix: true,
            })}
          </span>
          <a
            href={incident.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline hover:text-foreground"
            // R28: incident headlines name victim organisations, in an attribute.
            data-ph-no-capture
            aria-label={`Open ${incident.title} on upstream source`}
          >
            Source <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        </div>

        {cves.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {cves.slice(0, 5).map((cve) => (
              <Link key={cve} to={`/vulnerabilities/${cve}`} className="text-xs">
                <Badge variant="secondary">{cve}</Badge>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
